import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RiBox3Line } from 'react-icons/ri';
import { MdInventory } from 'react-icons/md';
import { IoTrendingDown, IoAnalyticsOutline } from 'react-icons/io5';
import { IoIosReturnLeft } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import toast from '../components/common/toast';
import Table from '../components/common/Table';
import SkeletonTable from '../components/SkeletonTable';
import { reportService, companyService } from '../services/api';
import {
 TotalProductsIcon,
 TotalBinsIcon,
 TotalProductsIcon as TotalUOMIcon, // agar alag UOM icon nahi hai to ye use kar lo
} from "../assets/states/StatsIcons";

const SummaryCard = ({
  label,
  value,
  Icon,
  bg,
  stroke = true,
  icon,
  iconBgColor = 'bg-gray-100',
  iconTextColor = 'text-gray-600',
}) => {
  const hasIconComponent = Icon && typeof Icon === 'function';
  const hasIconElement = icon != null && React.isValidElement(icon);

  const boxStyle = {
    width: 40,
    height: 40,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 6,
    ...(hasIconComponent && bg ? { background: bg } : {}),
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm w-full min-w-0">
      <div className="flex items-center gap-5">
        <div
          className={`flex items-center justify-center flex-shrink-0 ${hasIconElement ? iconBgColor : ''}`}
          style={boxStyle}
        >
          {hasIconComponent && (stroke ? <Icon size={18} color="#FFFFFF" /> : <Icon size={24} />)}
          {hasIconElement && <span className={iconTextColor}>{icon}</span>}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-semibold text-gray-900 leading-none">{value}</p>
          <p className="mt-2 text-sm font-medium text-gray-400 whitespace-nowrap truncate">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};


const REPORT_TABS = [
  { id: 'product', label: 'Inventory by Product' },
  { id: 'batch', label: 'Inventory by Batch' },
  // { id: 'uom', label: 'Inventory by UOM' },
  // { id: 'summary', label: 'Inventory by Summary' },
  { id: 'aging', label: 'Ageing Report' },
];

const PRODUCT_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'SKU Name' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'uom', label: 'UOM' },
  // { key: 'openingStock', label: 'Opening' },
  { key: 'inwardQuantity', label: 'Inward' },
  { key: 'outwardQuantity', label: 'Outward' },
  { key: 'returnQuantity', label: 'Return' },
  // { key: 'closingStock', label: 'Closing' },
];

const Pill = ({ text, variant = "blue" }) => {
 const map = {
   green: "border-emerald-500 text-emerald-600 bg-emerald-50/50",
   orange: "border-orange-300 text-orange-400 bg-orange-50/50",
   blue: "border-indigo-800 text-indigo-900 bg-indigo-50/40",
   gray: "border-gray-300 text-gray-700 bg-gray-50",
 };

 return (
   <span
     className={`inline-flex items-center justify-center rounded-full border px-4 py-1 text-sm font-medium whitespace-nowrap ${map[variant] || map.gray}`}
   >
     {text}
   </span>
 );
};

const BATCH_COLUMNS = [
 { key: 'productCode', label: 'Product Code' },
 { 
   key: 'companyName', 
   label: 'Company',
   render: (val) => val || '—'
 },
 { key: 'batchNumber', label: 'Batch Number' },
 {
   key: 'expiryDate',
   label: 'Exp Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 { key: 'quantity', label: 'Quantity', align: 'right' },

 // ✅ condition pill (Expired / Good)
 {
   key: 'condition',
   label: 'Condition',
   render: (val) => {
     const v = String(val || '').toLowerCase();
     const isExpired = v.includes('expired');
     const isGood = v.includes('good');
     return (
       <Pill
         text={val || '—'}
         variant={isExpired ? 'orange' : isGood ? 'green' : 'gray'}
       />
     );
   },
 },

 { key: 'binCode', label: 'Bin Code' },

 // ✅ binType pill (blue)
 {
   key: 'binType',
   label: 'Bin Type',
   render: (val) => <Pill text={val || '—'} variant="blue" />,
 },
];


// const UOM_COLUMNS = [
//   { key: 'uom', label: 'UOM' },
//   { key: 'productCode', label: 'Product Code' },
//   { key: 'productDescription', label: 'Description' },
//   { 
//     key: 'companyName', 
//     label: 'Company',
//     render: (val) => val || '—'
//   },
//   { key: 'qnt', label: 'Qnt', align: 'right' },
//   { key: 'holdQty', label: 'Hold Qnt', align: 'right' },
// ];

const EXPIRED_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  // { 
  //   key: 'shelfLifeRemainingPercent', 
  //   label: '% Remaining',
  //   align: 'right',
  //   render: (val) => val != null ? `${val}%` : '—'
  // },
  { key: 'quantity', label: 'Quantity', align: 'right' },
  { key: 'binCode', label: 'Bin Code' },
];

const NEAR_EXPIRY_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  { 
    key: 'shelfLifeRemainingPercent', 
    label: '% Remaining',
    align: 'right',
    render: (val) => val != null ? `${val}%` : '—'
  },
  { key: 'daysUntilExpiry', label: 'Days Left', align: 'right' },
  { key: 'quantity', label: 'Quantity', align: 'right' },
];

const CARD_TITLES = {
  product: 'Inventory Report (Product)',
  batch: 'Inventory Report (Batch)',
  // uom: 'Inventory Report (UOM)',
  // summary: 'Inventory Summary',
  aging: 'Ageing Report',
};

export function Reports() {
  const [activeReport, setActiveReport] = useState('product');
  const [downloading, setDownloading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;
  const [searchFilter] = useState('');
  const [startDateFilter] = useState('');
  const [endDateFilter] = useState('');
  const [debouncedFilters, setDebouncedFilters] = useState({
    companyId: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters({
        companyId: selectedCompanyId,
        search: searchFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedCompanyId, searchFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    setPage(1);
  }, [activeReport]);

  const reportFilters = useMemo(
    () => ({
      companyId: debouncedFilters.companyId || undefined,
      search: debouncedFilters.search?.trim() || undefined,
      startDate: debouncedFilters.startDate || undefined,
      endDate: debouncedFilters.endDate || undefined,
    }),
    [debouncedFilters]
  );

  const handleDownloadReport = async (reportType) => {
    try {
      setDownloading(true);
      let blob;
      let filename;
      const companyId = selectedCompanyId || undefined;

      switch (reportType) {
        case 'product':
          blob = await reportService.downloadInventoryReportByProduct(companyId);
          filename = `inventory_report_by_product_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'batch':
          blob = await reportService.downloadInventoryReportByBatch(companyId);
          filename = `inventory_report_by_batch_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'uom':
          blob = await reportService.downloadInventoryReportByUOM(companyId);
          filename = `inventory_report_by_uom_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'summary':
          blob = await reportService.downloadInventorySummary(companyId);
          filename = `inventory_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        case 'aging':
          blob = await reportService.downloadAgingReport(companyId);
          filename = `aging_report_${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        default:
          throw new Error('Invalid report type');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error?.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const getReports = useCallback(async ({ page: pageArg, limit: limitArg, filters }) => {
    const params = { page: pageArg, limit: limitArg, ...filters };
    if (activeReport === 'product') return reportService.getInventoryReportByProduct(params);
    if (activeReport === 'batch') return reportService.getInventoryReportByBatch(params);
    if (activeReport === 'aging') return reportService.getAgingReport(params);
    return { success: true, data: [] };
  }, [activeReport]);

  // const fetchInventoryByUOM = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventoryReportByUOM(companyId);
  //     if (result && result.success && Array.isArray(result.data)) {
  //       const flattened = [];
  //       for (const group of result.data) {
  //         for (const p of group.products || []) {
  //           flattened.push({
  //             uom: group.uom,
  //             productCode: p.productCode,
  //             productDescription: p.productDescription,
  //             companyName: p.companyName || '—',
  //             qnt: p.qnt || 0,
  //             holdQty: p.holdQty || 0,
  //           });
  //         }
  //       }
  //       setUomData(flattened);
  //     } else {
  //       setUomData([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory by UOM:', error);
  //     setUomData([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const fetchInventorySummary = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventorySummary(companyId);
  //     if (result && result.success && result.summary) {
  //       setSummaryData(result.summary);
  //     } else {
  //       setSummaryData(null);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory summary:', error);
  //     setSummaryData(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const { data: companiesResponse } = useQuery({
    queryKey: ['reportCompanies'],
    queryFn: () => companyService.getCompanies({ isActive: 'true' }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const companies = useMemo(() => companiesResponse?.data || [], [companiesResponse]);

  const {
    data: reportResponse,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['reports', activeReport, page, limit, reportFilters],
    queryFn: () => getReports({ page, limit, filters: reportFilters }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const loading = isLoading || isFetching;
  const productData = useMemo(
    () => (activeReport === 'product' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const batchData = useMemo(
    () => (activeReport === 'batch' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const expiredBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.expiredBatches) ? reportResponse.expiredBatches : []),
    [activeReport, reportResponse]
  );
  const nearExpiryBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.nearExpiryBatches) ? reportResponse.nearExpiryBatches : []),
    [activeReport, reportResponse]
  );
  const ageingSummary = useMemo(
    () => (activeReport === 'aging' ? reportResponse?.summary || null : null),
    [activeReport, reportResponse]
  );
  const totalCount = Number(reportResponse?.count || 0);
  const hasNextPage = totalCount > page * limit;

  return (
    <div
      className="w-full pl-3 pr-6 pt-2 pb-6 bg-gray-50 min-h-screen"
      style={{ minWidth: '800px', fontFamily: "'Nunito', sans-serif" }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-4 w-full"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 28,
          padding: '16px 24px',
          boxShadow: '0px 0px 4px 0px #959DA526',
        }}
      >
        <h1
          style={{
            fontFamily: 'Nunito',
            fontSize: 18,
            fontWeight: 600,
            color: '#161616',
            margin: 0,
          }}
        >
          Reports
        </h1>
        <div
          className="overflow-x-auto"
          style={{
            border: '1px solid #D0D5DD',
            borderRadius: 14,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: 6,
            gap: 6,
          }}
        >
          {REPORT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveReport(tab.id)}
              className="flex-shrink-0 whitespace-nowrap transition"
              style={{
                background: activeReport === tab.id ? 'rgba(24,135,66,0.08)' : 'transparent',
                color: activeReport === tab.id ? '#188742' : '#101828',
                fontWeight: activeReport === tab.id ? 600 : undefined,
                borderRadius: 12,
                padding: '10px 18px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 w-full rounded-2xl p-6 border border-[#18181B0F] bg-[#fcfcfc]">
      <div className="flex items-center justify-between mb-6">
  <h2 className="text-lg font-semibold text-gray-900">
    {CARD_TITLES[activeReport] || CARD_TITLES.product}
  </h2>

  <div className="flex items-center gap-[18px]">
    <div className="relative flex items-center">
      <select
        value={selectedCompanyId}
        onChange={(e) => setSelectedCompanyId(e.target.value)}
        className="rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold appearance-none cursor-pointer transition hover:bg-[#188742]/5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap pr-10"
        style={{ height: 48, paddingLeft: 22, paddingRight: 40, minWidth: 180 }}
      >
        <option value="">All Companies</option>
        {companies.map((company) => (
          <option key={company._id || company.id} value={company._id || company.id}>
            {company.companyCode ? `${company.companyCode} - ${company.name}` : company.name}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 flex items-center text-[#188742]" style={{ top: '50%', transform: 'translateY(-50%)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </span>
    </div>

    <button
      type="button"
      onClick={() => handleDownloadReport(activeReport)}
      disabled={downloading || loading}
      className="flex items-center gap-2 rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      style={{ height: 48, paddingLeft: 22, paddingRight: 22 }}
    >
      <FiDownload className="w-4 h-4" />
      {downloading ? "Downloading..." : "Export CSV"}
    </button>
  </div>
</div>


        {activeReport === 'product' && (
          loading ? (
            <SkeletonTable cols={PRODUCT_COLUMNS.length} rows={8} />
          ) : (
            <Table
              columns={PRODUCT_COLUMNS}
              data={productData}
              loading={false}
              emptyMessage="No data available"
            />
          )
        )}

        {activeReport === 'batch' && (
          loading ? (
            <SkeletonTable cols={BATCH_COLUMNS.length} rows={8} />
          ) : (
            <Table
              columns={BATCH_COLUMNS}
              data={batchData}
              loading={false}
              emptyMessage="No data available"
            />
          )
        )}

        {/* {activeReport === 'uom' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
  <SummaryCard
    label="Total Products Out of Stock"
    value={uomData.filter((r) => (r.qnt ?? 0) === 0).length}
    Icon={TotalProductsIcon}
    bg="#DC2626"   // red
    stroke={true}
  />

  <SummaryCard
    label="Total Products (in report)"
    value={uomData.length}
    Icon={TotalBinsIcon}
    bg="#01C36F"   // green
    stroke={true}
  />

  <SummaryCard
    label="Total UOMs"
    value={[...new Set(uomData.map((r) => r.uom))].length}
    Icon={TotalProductsIcon}
    bg="#4F46E5"   // blue/purple
    stroke={true}
  />
</div>

                <div className="mt-8">
                  <Table
                    columns={UOM_COLUMNS}
                    data={uomData}
                    loading={loading}
                    emptyMessage="No data available"
                  />
                </div>
              </>
            )}
          </>
        )} */}

        {/* {activeReport === 'summary' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : summaryData ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                <SummaryCard
                  label="Total Products"
                  value={summaryData.totalProducts ?? 0}
                  icon={<RiBox3Line className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Opening Stock"
                  value={summaryData.totalOpeningStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Inward"
                  value={summaryData.totalInwardQuantity ?? 0}
                  icon={<IoTrendingDown className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Outward"
                  value={summaryData.totalOutwardQuantity ?? 0}
                  icon={<IoAnalyticsOutline className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Return"
                  value={summaryData.totalReturnQuantity ?? 0}
                  icon={<IoIosReturnLeft className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Closing Stock"
                  value={summaryData.totalClosingStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-gray-500">No data available</div>
            )}
          </>
        )} */}

        {activeReport === 'aging' && (
          <div className="space-y-8">
            {loading ? (
              <>
                <SkeletonTable cols={EXPIRED_COLUMNS.length} rows={5} />
                <SkeletonTable cols={NEAR_EXPIRY_COLUMNS.length} rows={5} />
              </>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                  <SummaryCard
                    label="Expired Batches"
                    value={ageingSummary?.expiredBatchCount ?? expiredBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-red-50"
                    iconTextColor="text-red-600"
                  />
                  <SummaryCard
                    label="Near Expiry (30 Days)"
                    value={ageingSummary?.nearExpiryBatchCount ?? nearExpiryBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-amber-50"
                    iconTextColor="text-amber-600"
                  />
                  <SummaryCard
                    label="Good Batches"
                    value={ageingSummary?.goodBatchCount ?? 0}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-emerald-50"
                    iconTextColor="text-emerald-600"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Expired Batches</h3>
                  <Table
                    columns={EXPIRED_COLUMNS}
                    data={expiredBatches}
                    loading={loading}
                    emptyMessage="No expired batches"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Near Expiry Batches (Next 30 Days)</h3>
                  <Table
                    columns={NEAR_EXPIRY_COLUMNS}
                    data={nearExpiryBatches}
                    loading={loading}
                    emptyMessage="No near expiry batches"
                  />
                </div>
              </>
            )}
          </div>
        )}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page <= 1 || loading}
            className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">Page {page}</span>
          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={loading || !hasNextPage}
            className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
